import { ObSessionStateType } from '@central-products/interface-obsession-service';
import { AccountInfoStateType } from '@central-products/interface-account-info';
import { publish, publishSticky, subscribe, unsubscribe } from '@wh/message-bus';
import cookies, { erase, get, set } from 'browser-cookies';
import {
  ACCOUNT_INFO_STATE_CHANGED_EVENT,
  ACCOUNT_INFO_STATE_GET_COMMAND,
  ACCOUNT_INFO_STATE_GET_EVENT,
  OB_SESSION_STATE_GET_COMMAND,
  OB_SESSION_STATE_GET_EVENT,
  GOT_LOGIN_STATE_EVENT,
  SESSION_SYNCHRONIZED_EVENT,
  SESSION_SYNCHRONIZATION_ERROR,
} from '../../topics';
import { isDeviceNative } from '../../utils/deviceDetection';

const OB_COOKIES = ['cas_ssl_login', 'cas_login', 'cust_ssl_login'];

const publishError = (message, error) => {
  publish({
    topic: SESSION_SYNCHRONIZATION_ERROR,
    data: error
      ? {
          error,
          message,
        }
      : { message },
  });
};

const removeCookies = (options) => {
  OB_COOKIES.forEach((cookie) => {
    erase(`hrs_remove_cookie_${cookie}`, options);
    if (get(cookie)) {
      publishError(`cookie ${cookie} was present after logout`);
      set(`hrs_remove_cookie_${cookie}`, 'true', options);
      erase(cookie, options);
    }
  });
};

/**
 * Taken from Betslip codebase
 * https://git.nonprod.williamhill.plc/sportsbook/betslip-mono/-/blob/b845b6c00bf65767427ce2b970d6b9bc842af3d9/packages/modules/headless/src/observables/currency.js
 */
const CURRENCY_TO_OB_CURRENCY_MAP = {
  GBP: '&pound;',
  EUR: '&euro;',
  AUD: '$',
  CAD: 'C $',
  USD: '$',
  DKK: 'DKK&nbsp;',
  JPY: '&yen;',
  SGD: '$',
  HKD: '$',
  CHF: 'CHF&nbsp;',
};

const normalizeGotLoginStatePayload = (hasValidSession = false, accountInfo = {}) => {
  const {
    obAvFlags = '',
    firstName: obClientName = '',
    accountId: obAccountNumber = '',
    currency: obBalanceCurrency = '',
    currencyCode,
    balance: obBalanceAmount = '',
    obMessageNumber = '',
    obLastLogin = '',
    obFormAction = '',
    obFormHidden = '',
    obUsernameName = '',
    obPasswordName = '',
  } = accountInfo;

  return {
    isLoggedIn: hasValidSession,
    cust: {
      auth: cookies.get('cust_auth'),
      login: cookies.get('cust_login'),
      sslLogin: cookies.get('cust_ssl_login'),
    },
    data: {
      obAvFlags,
      obClientName,
      obAccountNumber,
      obBalanceCurrency: Object.prototype.hasOwnProperty.call(CURRENCY_TO_OB_CURRENCY_MAP, currencyCode)
        ? CURRENCY_TO_OB_CURRENCY_MAP[currencyCode]
        : obBalanceCurrency,
      obBalanceAmount,
      obMessageNumber,
      obLastLogin,
      obFormAction,
      obFormHidden,
      obUsernameName,
      obPasswordName,
    },
  };
};

const getAccountInfo = () => {
  let accountInfoReject;
  let accountInfoStateChangedSubscription;
  let accountInfoStateGetSubscription;

  return {
    done: new Promise((resolve, reject) => {
      accountInfoReject = reject;

      const onCompletedCallback = ({ data } = {}) => {
        if (data.state !== AccountInfoStateType.ACCOUNT_INFO_EXISTS) {
          return;
        }

        unsubscribe(accountInfoStateChangedSubscription);
        unsubscribe(accountInfoStateGetSubscription);

        resolve(data);
      };

      accountInfoStateChangedSubscription = subscribe({
        topic: ACCOUNT_INFO_STATE_CHANGED_EVENT,
        callback: onCompletedCallback,
      });
      accountInfoStateGetSubscription = subscribe({
        topic: ACCOUNT_INFO_STATE_GET_EVENT,
        callback: onCompletedCallback,
      });

      publish(ACCOUNT_INFO_STATE_GET_COMMAND, {});
    }),
    cancel: () => {
      if (accountInfoReject) {
        accountInfoReject();
      }

      if (accountInfoStateChangedSubscription) {
        unsubscribe(accountInfoStateChangedSubscription);
      }

      if (accountInfoStateGetSubscription) {
        unsubscribe(accountInfoStateGetSubscription);
      }
    },
  };
};

let accountInfoPromise;

export const handleOBSessionStateChanged =
  ({ jurisdiction, domain, config }) =>
  async ({ data } = {}) => {
    if (accountInfoPromise) {
      accountInfoPromise.cancel();
      accountInfoPromise = undefined;
    }

    if (data.state !== ObSessionStateType.SESSION_REQUESTED) {
      publish({
        topic: SESSION_SYNCHRONIZED_EVENT,
        data: { sessionSynchronized: data.state === ObSessionStateType.SESSION_EXISTS },
      });
    }

    if (data.state === ObSessionStateType.SESSION_EXISTS) {
      accountInfoPromise = getAccountInfo();

      try {
        const accountInfo = await accountInfoPromise.done;
        publishSticky(GOT_LOGIN_STATE_EVENT, normalizeGotLoginStatePayload(true, accountInfo));
      } catch (error) {}
    }

    if (data.state === ObSessionStateType.NO_SESSION) {
      publishSticky(GOT_LOGIN_STATE_EVENT, normalizeGotLoginStatePayload(false));
    }

    if (jurisdiction === 'com' && isDeviceNative()) {
      return;
    }

    if (data.state === ObSessionStateType.NO_SESSION && config.removeCookie) {
      removeCookies({
        domain,
        path: '/',
      });
    }
  };

export const handleRequestGotLoginState = () => {
  let obSessionState;
  let accountInfoState;

  const onCompletedCallback = () => {
    publishSticky(
      GOT_LOGIN_STATE_EVENT,
      normalizeGotLoginStatePayload(obSessionState.state === ObSessionStateType.SESSION_EXISTS, accountInfoState)
    );
  };
  const obSessionStateGetSubscription = subscribe({
    topic: OB_SESSION_STATE_GET_EVENT,
    callback: ({ data } = {}) => {
      obSessionState = data;
      unsubscribe(obSessionStateGetSubscription);

      if (accountInfoState) {
        onCompletedCallback();
      }
    },
  });
  const accountInfoStateGetSubscription = subscribe({
    topic: ACCOUNT_INFO_STATE_GET_EVENT,
    callback: ({ data } = {}) => {
      accountInfoState = data;
      unsubscribe(accountInfoStateGetSubscription);

      if (obSessionState) {
        onCompletedCallback();
      }
    },
  });

  publish(OB_SESSION_STATE_GET_COMMAND, {});
  publish(ACCOUNT_INFO_STATE_GET_COMMAND, {});
};
